import { z } from 'zod';
import { CONDITIONS, LOCALES } from './i18n';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
export const MAX_PRICE = 10_000_000; // £100,000.00 in minor units
export const moneySchema = z
  .number({ message: 'Enter a price' })
  .int('Price must be in minor units')
  .min(0, 'Price cannot be negative')
  .max(MAX_PRICE, 'Price is too high');
export const slugSchema = z
  .string()
  .trim()
  .min(1, 'Required')
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and hyphens');
const optionalText = (max: number) => z.string().trim().max(max).default('');
const urlOrPath = z
  .string()
  .trim()
  .max(1000)
  .refine((v) => v === '' || v.startsWith('/') || /^https?:\/\//.test(v), 'Must be a URL or a path starting with /');

export const PRODUCT_STATUSES = ['draft', 'published', 'archived'] as const;
export const productStatusSchema = z.enum(PRODUCT_STATUSES);
export type ProductStatus = z.infer<typeof productStatusSchema>;

export const ROLES = ['owner', 'admin', 'staff'] as const;
export const roleSchema = z.enum(ROLES);
export type Role = z.infer<typeof roleSchema>;

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------
export const productOptionSchema = z.object({
  name: z.string().trim().min(1, 'Option name required').max(40),
  values: z
    .array(z.string().trim().min(1).max(60))
    .min(1, 'Add at least one value')
    .max(40)
    .refine((v) => new Set(v.map((s) => s.toLowerCase())).size === v.length, 'Values must be unique'),
});
export type ProductOption = z.infer<typeof productOptionSchema>;

export const variantInputSchema = z
  .object({
    id: z.string().uuid().optional(),
    options: z.record(z.string(), z.string()),
    price: moneySchema,
    salePrice: moneySchema.nullable().default(null),
    sku: optionalText(64),
    stock: z.number().int().min(0, 'Stock cannot be negative').max(1_000_000),
    imageMediaId: z.string().uuid().nullable().optional(),
  })
  .refine((v) => v.salePrice === null || v.salePrice < v.price, {
    message: 'Sale price must be lower than the price',
    path: ['salePrice'],
  });
export type VariantInput = z.infer<typeof variantInputSchema>;

export const productImageInputSchema = z.object({
  mediaId: z.string().uuid(),
  alt: optionalText(200),
});

export const conditionSchema = z.enum(CONDITIONS);
export const localeSchema = z.enum(LOCALES);

/** Free-text details shown in the "About this item" table. */
export const productAttributesSchema = z.object({
  origin: optionalText(80),
  material: optionalText(120),
  dimensions: optionalText(120),
  conditionNote: optionalText(400),
});
export type ProductAttributes = z.infer<typeof productAttributesSchema>;
const EMPTY_ATTRIBUTES: ProductAttributes = { origin: '', material: '', dimensions: '', conditionNote: '' };

const boundedRecord = <V extends z.ZodType>(value: V, max: number) =>
  z.record(z.string().max(60), value).refine((r) => Object.keys(r).length <= max, `Up to ${max} entries`);

/** Lithuanian (or any other non-default language) copy for a product. Empty fields fall back to English. */
export const productTranslationSchema = z.object({
  title: optionalText(160),
  description: optionalText(20_000),
  seoTitle: optionalText(160),
  seoDescription: optionalText(320),
  attributes: productAttributesSchema.default(EMPTY_ATTRIBUTES),
  /** Keyed by the English option name: { Colour: { name: 'Spalva', values: { Green: 'Žalia' } } } */
  options: boundedRecord(z.object({ name: optionalText(40), values: boundedRecord(z.string().trim().max(60), 40).default({}) }), 3).default({}),
});
export type ProductTranslation = z.infer<typeof productTranslationSchema>;
export const productTranslationsSchema = z.object({ lt: productTranslationSchema.optional() }).default({});
export type ProductTranslations = z.infer<typeof productTranslationsSchema>;

export const decadeSchema = z
  .number()
  .int()
  .min(1800, 'Choose a decade')
  .max(2030, 'Choose a decade')
  .refine((d) => d % 10 === 0, 'Use the first year of the decade, e.g. 1970');

/** A Vinted listing on any of Vinted's country sites, e.g. https://www.vinted.lt/items/1234567890-enamel-mug */
export const VINTED_URL = /^https:\/\/(www\.)?vinted\.[a-z]{2,3}(\.[a-z]{2})?\/items\/\d+[\w-]*\/?(\?[^\s]*)?$/i;
export const vintedUrlSchema = z
  .string()
  .trim()
  .max(500)
  .refine((v) => v === '' || VINTED_URL.test(v), 'Paste the link of a Vinted listing, like https://www.vinted.lt/items/1234567890-…')
  .transform((v) => v || null);

export const productInputSchema = z
  .object({
    title: z.string().trim().min(1, 'Title is required').max(160),
    slug: slugSchema.optional().or(z.literal('')),
    description: optionalText(20_000),
    status: productStatusSchema.default('draft'),
    featured: z.boolean().default(false),
    price: moneySchema,
    salePrice: moneySchema.nullable().default(null),
    sku: optionalText(64),
    stock: z.number().int().min(0, 'Stock cannot be negative').max(1_000_000).default(0),
    trackInventory: z.boolean().default(true),
    categoryIds: z.array(z.string().uuid()).max(20).default([]),
    collectionIds: z.array(z.string().uuid()).max(50).default([]),
    tags: z.array(z.string().trim().min(1).max(40)).max(40).default([]),
    options: z.array(productOptionSchema).max(3, 'Up to 3 options').default([]),
    variants: z.array(variantInputSchema).max(250).default([]),
    images: z.array(productImageInputSchema).max(30).default([]),
    seoTitle: optionalText(160),
    seoDescription: optionalText(320),
    decade: decadeSchema.nullable().default(null),
    condition: conditionSchema.nullable().default(null),
    attributes: productAttributesSchema.default(EMPTY_ATTRIBUTES),
    translations: productTranslationsSchema,
    vintedUrl: vintedUrlSchema.nullable().default(null),
  })
  .superRefine((p, ctx) => {
    if (p.salePrice !== null && p.salePrice >= p.price) {
      ctx.addIssue({ code: 'custom', message: 'Sale price must be lower than the price', path: ['salePrice'] });
    }
    const optionNames = p.options.map((o) => o.name.toLowerCase());
    if (new Set(optionNames).size !== optionNames.length) {
      ctx.addIssue({ code: 'custom', message: 'Option names must be unique', path: ['options'] });
    }
    if (p.options.length === 0) return;
    if (p.variants.length === 0) {
      ctx.addIssue({ code: 'custom', message: 'Add at least one variant', path: ['variants'] });
      return;
    }
    const seen = new Set<string>();
    p.variants.forEach((v, i) => {
      for (const opt of p.options) {
        const val = v.options[opt.name];
        if (!val || !opt.values.includes(val)) {
          ctx.addIssue({ code: 'custom', message: `Invalid value for ${opt.name}`, path: ['variants', i, 'options'] });
        }
      }
      const key = p.options.map((o) => v.options[o.name]).join(' / ');
      if (seen.has(key)) ctx.addIssue({ code: 'custom', message: `Duplicate variant ${key}`, path: ['variants', i] });
      seen.add(key);
    });
  });
export type ProductInput = z.infer<typeof productInputSchema>;
export type ProductInputRaw = z.input<typeof productInputSchema>;

export const BULK_ACTIONS = ['publish', 'unpublish', 'archive', 'delete', 'feature', 'unfeature'] as const;
export const bulkProductActionSchema = z.object({
  ids: z.array(z.string().uuid()).min(1, 'Select at least one product').max(500),
  action: z.enum(BULK_ACTIONS),
});

export const adminProductQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum([...PRODUCT_STATUSES, 'all']).optional(),
  categoryId: z.string().uuid().optional(),
  collectionId: z.string().uuid().optional(),
  stock: z.enum(['low', 'out', 'in']).optional(),
  sort: z.enum(['updated', 'created', 'title', 'price', 'stock']).default('updated'),
  dir: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export const STORE_SORTS = ['featured', 'newest', 'price-asc', 'price-desc', 'name'] as const;
export const storeProductQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  category: z.string().trim().max(120).optional(),
  collection: z.string().trim().max(120).optional(),
  tags: z.string().trim().max(500).optional(), // comma separated
  minPrice: z.coerce.number().int().min(0).optional(),
  maxPrice: z.coerce.number().int().min(0).optional(),
  inStock: z.enum(['true', 'false']).optional(),
  onSale: z.enum(['true', 'false']).optional(),
  decade: z.string().trim().max(100).optional(), // comma separated, e.g. "1960,1970"
  condition: z.string().trim().max(100).optional(), // comma separated condition keys
  sort: z.enum(STORE_SORTS).default('featured'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(48).default(12),
});
export type StoreProductQuery = z.infer<typeof storeProductQuerySchema>;

// ---------------------------------------------------------------------------
// Taxonomy
// ---------------------------------------------------------------------------
export const taxonomyTranslationSchema = z.object({ name: optionalText(80), description: optionalText(2000) });
export const taxonomyInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(80),
  slug: slugSchema.optional().or(z.literal('')),
  description: optionalText(2000),
  imageUrl: urlOrPath.nullable().default(null),
  position: z.number().int().min(0).max(10_000).default(0),
  featured: z.boolean().default(false),
  translations: z.object({ lt: taxonomyTranslationSchema.optional() }).default({}),
});
export type TaxonomyInput = z.infer<typeof taxonomyInputSchema>;
export type TaxonomyTranslations = TaxonomyInput['translations'];

// ---------------------------------------------------------------------------
// Auth + team
// ---------------------------------------------------------------------------
export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required').max(200),
});
export const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(200)
  .refine((p) => /[A-Za-z]/.test(p) && /[0-9]/.test(p), 'Include letters and numbers');
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Required'),
  newPassword: passwordSchema,
});
export const userCreateSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(80),
  email: z.string().trim().toLowerCase().email('Enter a valid email'),
  role: roleSchema,
  password: passwordSchema,
});
export const userUpdateSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  role: roleSchema.optional(),
  active: z.boolean().optional(),
  password: passwordSchema.optional(),
});

// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------
/** Few enough left to warn about in the admin. */
export const LOW_STOCK_THRESHOLD = 2;

export const stockUpdateSchema = z.object({
  mode: z.enum(['set', 'adjust']).default('set'),
  value: z.number().int().min(-1_000_000).max(1_000_000),
});

// ---------------------------------------------------------------------------
// Content pages
// ---------------------------------------------------------------------------
export const pageTranslationSchema = z.object({
  title: optionalText(160),
  body: optionalText(50_000),
  seoTitle: optionalText(160),
  seoDescription: optionalText(320),
});
export const pageInputSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(160),
  slug: slugSchema.optional().or(z.literal('')),
  body: optionalText(50_000),
  published: z.boolean().default(true),
  seoTitle: optionalText(160),
  seoDescription: optionalText(320),
  translations: z.object({ lt: pageTranslationSchema.optional() }).default({}),
});
export type PageInput = z.infer<typeof pageInputSchema>;
export type PageTranslations = PageInput['translations'];

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #8a4b2e');

export const storeSettingsSchema = z.object({
  name: z.string().trim().min(1, 'Store name is required').max(80),
  tagline: optionalText(160),
  email: z.string().trim().email('Enter a valid email').or(z.literal('')).default(''),
  phone: optionalText(40),
  currency: z.enum(['EUR', 'GBP', 'USD']).default('EUR'),
  logoUrl: urlOrPath.nullable().default(null),
  accentColor: hexColor.default('#8a4b2e'),
  announcement: z
    .object({ enabled: z.boolean().default(true), text: optionalText(160), link: urlOrPath.default('') })
    .default({ enabled: false, text: '', link: '' }),
});
export type StoreSettings = z.infer<typeof storeSettingsSchema>;

export const seoSettingsSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(120),
  titleTemplate: z
    .string()
    .trim()
    .max(120)
    .refine((v) => v.includes('%s'), 'Must include %s where the page title goes')
    .default('%s'),
  description: optionalText(320),
  ogImageUrl: urlOrPath.nullable().default(null),
});
export type SeoSettings = z.infer<typeof seoSettingsSchema>;

export const SETTINGS_KEYS = ['store', 'seo'] as const;
export type SettingsKey = (typeof SETTINGS_KEYS)[number];
export const settingsSchemas = {
  store: storeSettingsSchema,
  seo: seoSettingsSchema,
} as const;
export interface AllSettings {
  store: StoreSettings;
  seo: SeoSettings;
}

// ---------------------------------------------------------------------------
// Settings translations — only shopper-facing copy is translatable. Empty strings
// fall back to the English value, so a half-finished translation never blanks the site.
// ---------------------------------------------------------------------------
const tx = (max: number) => z.string().trim().max(max).default('');

export const storeSettingsTranslationSchema = z.object({
  name: tx(80),
  tagline: tx(160),
  announcement: z.object({ text: tx(160) }).prefault({}),
});
export const seoSettingsTranslationSchema = z.object({
  title: tx(120),
  titleTemplate: tx(120).refine((v) => v === '' || v.includes('%s'), 'Must include %s where the page title goes'),
  description: tx(320),
});
export const settingsTranslationSchemas = {
  store: storeSettingsTranslationSchema,
  seo: seoSettingsTranslationSchema,
} as const;
export interface SettingsTranslation {
  store: z.infer<typeof storeSettingsTranslationSchema>;
  seo: z.infer<typeof seoSettingsTranslationSchema>;
}
